/**
 * components/admin/DeliveryDispatchBoard.jsx · v2.7.57+E34 · Section 3
 * ═══════════════════════════════════════════════════════════════════════
 * DELIVERY SYSTEM · Section 3 · Taxi-Commander Admin Panel.
 *
 * Renders the real-time dispatch queue table (customer + stops + pickup
 * time + financial status + driver-assignment dropdown) and the isolated
 * Document Center dialog per delivery (receipts, itemized breakdown,
 * payment authorization details).
 *
 * RBAC:
 *   - Any staff read-tier can VIEW the board and open Document Centers
 *   - Only Dispatcher / Supervisor / Operating Partner / Developer can
 *     assign or un-assign drivers (backend enforces via
 *     _require_dispatch_write)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";
import { fmtUsd } from "@/lib/format";
import { toast } from "sonner";
import {
  Truck, Search, RefreshCw, User, Phone, Mail, MapPin, Clock, Hash,
  FileImage, CreditCard, X, ExternalLink, ArrowRight, Package,
  CheckCircle2, AlertCircle, DollarSign, Edit3, StickyNote, Send,
  ShieldAlert, MessageSquare,
} from "lucide-react";

const STATUS_TABS = [
  { key: "ALL",                  label: "All"          },
  { key: "PENDING_QUOTE",        label: "Pending Quote", tone: "zinc"    },
  { key: "QUOTED",               label: "Quoted",        tone: "sky"     },
  { key: "AUTHORIZED_PROCESSING",label: "Authorized",    tone: "emerald" },
  { key: "DISPATCHED",           label: "Dispatched",    tone: "amber"   },
  { key: "IN_PROGRESS",          label: "In Progress",   tone: "amber"   },
  { key: "DELIVERED",            label: "Delivered",     tone: "emerald" },
  { key: "MANUALLY_CLOSED",      label: "Manual Closed", tone: "violet"  },
];

const STATUS_CHIP = {
  PENDING_QUOTE:         "bg-zinc-800    text-zinc-300     border-zinc-600",
  QUOTED:                "bg-sky-500/15  text-sky-300      border-sky-400/40",
  AUTHORIZED_PROCESSING: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  DISPATCHED:            "bg-amber-500/15 text-amber-300  border-amber-400/40",
  IN_PROGRESS:           "bg-amber-500/25 text-amber-200   border-amber-400/60",
  DELIVERED:             "bg-emerald-500/25 text-emerald-200 border-emerald-400/60",
  MANUALLY_CLOSED:       "bg-violet-500/20 text-violet-300  border-violet-400/50",
  CANCELLED:             "bg-red-500/15   text-red-300     border-red-400/40",
};

// v2.7.65+E51 · statuses from which an admin may manually close a delivery
const MANUAL_CLOSE_FROM = ["PENDING_QUOTE", "QUOTED", "AUTHORIZED_PROCESSING", "DISPATCHED", "IN_PROGRESS"];


export default function DeliveryDispatchBoard() {
  const [tab, setTab] = useState("ALL");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const activeRow = useMemo(() => items.find((i) => i.id === activeId) || null, [items, activeId]);

  const load = useCallback(async (opts = {}) => {
    const isRefresh = !!opts.refresh;
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== "ALL") params.set("status", tab);
      if (q) params.set("q", q);
      params.set("limit", "500");
      const r = await adminApi.get(`/deliveries/admin/list?${params}`);
      setItems(r.data?.items || []);
      setStats(r.data?.stats || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to load dispatch queue");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tab, q]);

  const loadDrivers = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/drivers?role=driver");
      const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      // Keep only active, non-deleted drivers — matches backend assign gate
      setDrivers(arr.filter((d) => !d.deleted_at));
    } catch (e) {
      console.warn("[dispatch] driver list load failed:", e?.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDrivers(); }, [loadDrivers]);

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="delivery-dispatch-board">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-amber-400/15 border border-amber-400/40 flex items-center justify-center">
            <Package className="h-4 w-4 text-amber-300"/>
          </div>
          <div>
            <div className="text-sm font-bold text-zinc-100">Dispatch Queue</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Multi-stop delivery board · Live</div>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => { load({ refresh: true }); loadDrivers(); }}
          disabled={refreshing}
          className="h-8 text-xs"
          data-testid="dispatch-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`}/>Refresh
        </Button>
      </div>

      {/* Stats banner */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-3">
        <MiniStat label="Total"        value={stats.total ?? 0} testid="stat-total"/>
        <MiniStat label="Pending"      value={stats.pending_quote ?? 0} tone="zinc"    testid="stat-pending"/>
        <MiniStat label="Quoted"       value={stats.quoted ?? 0}        tone="sky"     testid="stat-quoted"/>
        <MiniStat label="Authorized"   value={stats.authorized ?? 0}    tone="emerald" testid="stat-authorized"/>
        <MiniStat label="Dispatched"   value={stats.dispatched ?? 0}    tone="amber"   testid="stat-dispatched"/>
        <MiniStat label="In Progress"  value={stats.in_progress ?? 0}   tone="amber"   testid="stat-in-progress"/>
        <MiniStat label="Delivered $"  value={fmtUsd(stats.total_authorized_amt ?? 0)} tone="emerald" testid="stat-authorized-amt"/>
      </div>

      {/* Filter tabs + search */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-2.5 py-1 rounded text-[11px] uppercase font-bold tracking-wider border transition ${
              tab === t.key
                ? "bg-amber-400 border-amber-400 text-zinc-950"
                : "bg-zinc-950 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            }`}
            data-testid={`dispatch-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
        <div className="flex-1 min-w-[200px] relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"/>
          <Input
            placeholder="Search customer / email / phone / business…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="bg-zinc-950 border-zinc-700 h-8 pl-7 text-xs"
            data-testid="dispatch-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center text-zinc-500 text-sm" data-testid="dispatch-loading">Loading queue…</div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-zinc-500 text-sm border border-dashed border-zinc-800 rounded" data-testid="dispatch-empty">
          No delivery requests match the current filter.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2" data-testid="dispatch-table-wrap">
          <table className="w-full text-xs" data-testid="dispatch-table">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-2 py-2">Customer</th>
                <th className="text-left px-2 py-2">Requested Pickup</th>
                <th className="text-center px-2 py-2">Stops</th>
                <th className="text-right px-2 py-2">Quote</th>
                <th className="text-center px-2 py-2">Payment</th>
                <th className="text-center px-2 py-2">Status</th>
                <th className="text-left px-2 py-2">Driver</th>
                <th className="text-center px-2 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <DispatchRow
                  key={row.id}
                  row={row}
                  drivers={drivers}
                  onOpen={() => setActiveId(row.id)}
                  onChanged={() => load({ refresh: true })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Document Center */}
      <Dialog open={!!activeRow} onOpenChange={(o) => { if (!o) setActiveId(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-y-auto">
          {activeRow && (
            <DocumentCenter
              row={activeRow}
              drivers={drivers}
              onClose={() => setActiveId(null)}
              onChanged={() => load({ refresh: true })}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function DispatchRow({ row, drivers, onOpen, onChanged }) {
  const [assigning, setAssigning] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState("");
  const [pinging, setPinging] = useState(false);

  const canAssign = row.status === "QUOTED" || row.status === "AUTHORIZED_PROCESSING";
  const canUnassign = !!row.assigned_driver_id
    && !["IN_PROGRESS", "DELIVERED"].includes(row.status);
  const pickupPretty = row.pickup_requested_at
    ? new Date(row.pickup_requested_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "ASAP";
  const paymentChip = row.payment_id
    ? { text: "AUTH ✓", tone: "emerald" }
    : row.status === "QUOTED"
    ? { text: "READY",  tone: "sky" }
    : row.status === "PENDING_QUOTE"
    ? { text: "—",      tone: "zinc" }
    : { text: "—",      tone: "zinc" };

  const doAssign = async () => {
    if (!selectedDriver) return;
    setAssigning(true);
    try {
      const r = await adminApi.post(`/deliveries/${row.id}/assign-driver`, { driver_id: selectedDriver });
      toast.success(`Assigned to ${r.data.assigned_driver_name} · status → ${r.data.status}`);
      setSelectedDriver("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const doUnassign = async () => {
    if (!window.confirm(`Un-assign ${row.assigned_driver_name} from this delivery?`)) return;
    setAssigning(true);
    try {
      const r = await adminApi.post(`/deliveries/${row.id}/unassign-driver`);
      toast.success(`Driver released · status → ${r.data.status}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Unassign failed");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <tr className="border-t border-zinc-800 hover:bg-zinc-800/30 align-top" data-testid={`dispatch-row-${row.id}`}>
      <td className="px-2 py-2">
        <div className="font-semibold text-zinc-100">{row.customer_name}</div>
        <div className="text-[10px] text-zinc-500 flex items-center gap-1"><Phone className="h-2.5 w-2.5"/>{row.customer_phone}</div>
        <div className="text-[10px] text-zinc-500 flex items-center gap-1"><Mail className="h-2.5 w-2.5"/>{row.customer_email}</div>
      </td>
      <td className="px-2 py-2 text-zinc-300 whitespace-nowrap">
        <div className="flex items-center gap-1 text-[11px]"><Clock className="h-2.5 w-2.5"/>{pickupPretty}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">{new Date(row.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} received</div>
      </td>
      <td className="px-2 py-2 text-center">
        <span className="inline-flex items-center gap-1 font-bold text-zinc-200">
          <Package className="h-3 w-3 text-amber-300"/>{row.stop_count}
        </span>
      </td>
      <td className="px-2 py-2 text-right meter-display">
        {row.quote_total != null
          ? <span className="font-bold text-emerald-300">{fmtUsd(row.quote_total)}</span>
          : <span className="text-zinc-500">—</span>}
      </td>
      <td className="px-2 py-2 text-center">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${
          paymentChip.tone === "emerald" ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-300"
          : paymentChip.tone === "sky"   ? "bg-sky-500/15 border-sky-400/40 text-sky-300"
          : "bg-zinc-800 border-zinc-600 text-zinc-400"
        }`}>
          <CreditCard className="h-2.5 w-2.5"/>{paymentChip.text}
        </span>
      </td>
      <td className="px-2 py-2 text-center">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${STATUS_CHIP[row.status] || STATUS_CHIP.PENDING_QUOTE}`} data-testid={`dispatch-row-${row.id}-status`}>
          {(row.status || "PENDING_QUOTE").replace(/_/g, " ")}
        </span>
      </td>
      <td className="px-2 py-2">
        {row.assigned_driver_id ? (
          <div className="flex items-center gap-1">
            <User className="h-3 w-3 text-emerald-300"/>
            <div>
              <div className="text-[11px] font-bold text-zinc-200">{row.assigned_driver_name}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <button
                  type="button"
                  data-testid={`dispatch-row-${row.id}-ping`}
                  disabled={pinging}
                  onClick={async () => {
                    setPinging(true);
                    try {
                      const r = await adminApi.post(`/admin/drivers/${row.assigned_driver_id}/ping`);
                      const chans = [];
                      if (r.data?.socket_open)   chans.push("WS");
                      if (r.data?.web_push_sent) chans.push(`push × ${r.data.web_push_sent}`);
                      if (r.data?.sms_sent)      chans.push("SMS");
                      toast.success(`Pinged ${row.assigned_driver_name} · ${chans.join(" + ") || "no channel"}`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Ping failed");
                    } finally {
                      setPinging(false);
                    }
                  }}
                  className="text-[10px] text-sky-300 hover:text-sky-200 underline underline-offset-2 disabled:opacity-50"
                >
                  {pinging ? "Pinging…" : "Ping"}
                </button>
                {canUnassign && (
                  <button
                    type="button"
                    onClick={doUnassign}
                    disabled={assigning}
                    className="text-[10px] text-red-400 hover:text-red-300 underline underline-offset-2"
                    data-testid={`dispatch-row-${row.id}-unassign`}
                  >
                    Release
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : canAssign ? (
          <div className="flex items-center gap-1">
            <select
              value={selectedDriver}
              onChange={(e) => setSelectedDriver(e.target.value)}
              disabled={assigning}
              className="bg-zinc-950 border border-zinc-700 rounded text-[11px] px-1.5 py-1 text-zinc-200 max-w-[160px]"
              data-testid={`dispatch-row-${row.id}-driver-select`}
            >
              <option value="">Assign driver…</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name || d.email?.split("@")[0]}{d.active === false ? " · pending" : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={doAssign}
              disabled={!selectedDriver || assigning}
              className="text-[10px] font-bold uppercase px-1.5 py-1 rounded bg-amber-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-zinc-950 hover:bg-amber-300 transition"
              data-testid={`dispatch-row-${row.id}-assign-btn`}
            >
              {assigning ? "…" : "Go"}
            </button>
          </div>
        ) : (
          <span className="text-[10px] text-zinc-600">—</span>
        )}
      </td>
      <td className="px-2 py-2 text-center">
        <div className="flex flex-col items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={onOpen}
            className="h-7 text-[10px]"
            data-testid={`dispatch-row-${row.id}-open`}
          >
            Documents
          </Button>
          {/* v2.7.65+E51 · manual closeout override for active deliveries */}
          {MANUAL_CLOSE_FROM.includes(row.status) && (
            <ManualCloseButton row={row} onChanged={onChanged}/>
          )}
        </div>
      </td>
    </tr>
  );
}


/* ── v2.7.65+E51 · Manual Closeout modal ──────────────────────────────
 * Admin enters the three final numbers directly — Final Client Price,
 * Final Driver Payout, Platform Fee. Bypasses every automatic distance/
 * time/token-credit calculation. On submit the delivery becomes
 * MANUALLY_CLOSED and the numbers flow to the Delivery Payout Ledger,
 * the Earnings Compliance Ledger and the driver's daily deposit math,
 * all tagged source_type=MANUAL_OVERRIDE.                              */
function ManualCloseButton({ row, onChanged }) {
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState("");
  const [payout, setPayout] = useState("");
  const [fee, setFee] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const num = (v) => (v === "" || Number.isNaN(Number(v)) ? null : Number(v));
  const variance = (num(price) != null && num(payout) != null && num(fee) != null)
    ? Math.round((num(price) - num(payout) - num(fee)) * 100) / 100
    : null;

  const openModal = () => {
    // Pre-fill from the live quote where available — admin overrides freely.
    setPrice(row.quote_total != null ? String(row.quote_total) : "");
    setPayout(""); setFee(""); setReason("");
    setOpen(true);
  };

  const submit = async () => {
    if (num(price) == null || num(payout) == null || num(fee) == null) {
      toast.error("Enter all three amounts (0 is allowed).");
      return;
    }
    if (num(price) < 0 || num(payout) < 0 || num(fee) < 0) {
      toast.error("Amounts cannot be negative.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/deliveries/admin/${row.id}/manual-close`, {
        final_client_price: num(price),
        final_driver_payout: num(payout),
        platform_fee: num(fee),
        reason: reason.trim() || null,
      });
      toast.success(`Delivery manually closed · ${fmtUsd(data.final_client_price)} posted to ledgers`, {
        description: data.compliance_logged
          ? "Payout + Earnings Compliance ledgers updated (MANUAL_OVERRIDE)"
          : "Payout ledger updated — compliance entry already existed or deferred",
      });
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manual closeout failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={openModal}
        className="h-7 text-[10px] border-violet-500/50 text-violet-300 hover:bg-violet-500/15 hover:text-violet-200"
        data-testid={`dispatch-row-${row.id}-manual-close`}
      >
        Close Delivery Manually
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-700 text-zinc-100 sm:max-w-md" data-testid="manual-close-dialog">
          <DialogHeader>
            <DialogTitle className="text-violet-300 flex items-center gap-2">
              <DollarSign className="h-5 w-5"/>
              Manual Closeout · #{(row.id || "").slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          <div className="text-[11px] text-zinc-500 -mt-1">
            Bypasses all automatic distance/time/credit calculations. Entries post to the
            Payout Ledger, Earnings Compliance and daily deposits as <span className="text-violet-300 font-bold">MANUAL_OVERRIDE</span>.
          </div>
          <div className="space-y-3 mt-1">
            <div>
              <Label className="text-[11px] text-zinc-400">Final Client Price (collected from client)</Label>
              <Input type="number" min="0" step="0.01" value={price}
                     onChange={(e) => setPrice(e.target.value)}
                     className="bg-zinc-900 border-zinc-700 mt-1"
                     data-testid="manual-close-price-input"/>
            </div>
            <div>
              <Label className="text-[11px] text-zinc-400">Final Driver Payout (paid to driver)</Label>
              <Input type="number" min="0" step="0.01" value={payout}
                     onChange={(e) => setPayout(e.target.value)}
                     className="bg-zinc-900 border-zinc-700 mt-1"
                     data-testid="manual-close-payout-input"/>
            </div>
            <div>
              <Label className="text-[11px] text-zinc-400">Platform Fee (net platform margin)</Label>
              <Input type="number" min="0" step="0.01" value={fee}
                     onChange={(e) => setFee(e.target.value)}
                     className="bg-zinc-900 border-zinc-700 mt-1"
                     data-testid="manual-close-fee-input"/>
            </div>
            {variance != null && variance !== 0 && (
              <div className="text-[11px] text-amber-300/90" data-testid="manual-close-variance">
                Unallocated remainder (price − payout − fee): {fmtUsd(variance)} — allowed, shown on the ledger entry.
              </div>
            )}
            <div>
              <Label className="text-[11px] text-zinc-400">Reason / note (optional)</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))}
                        className="bg-zinc-900 border-zinc-700 mt-1 min-h-[60px]"
                        placeholder="Why is this delivery being closed manually?"
                        data-testid="manual-close-reason-input"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-8 text-xs"
                    data-testid="manual-close-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy}
                    className="h-8 text-xs bg-violet-600 hover:bg-violet-500 text-white"
                    data-testid="manual-close-submit-btn">
              {busy ? "Closing…" : "Close Delivery"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function DocumentCenter({ row, drivers, onClose, onChanged }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Package className="h-4 w-4 text-amber-300"/>
          Document Center · {row.customer_name}
          <Badge variant="outline" className={`ml-auto text-[10px] ${STATUS_CHIP[row.status] || STATUS_CHIP.PENDING_QUOTE}`} data-testid="doc-status">
            {(row.status || "PENDING_QUOTE").replace(/_/g, " ")}
          </Badge>
        </DialogTitle>
      </DialogHeader>

      {/* Customer identity strip */}
      <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
        <IdField label="Reference ID"  value={row.id.slice(0, 8) + "…"} mono/>
        <IdField label="Requested Pickup" value={row.pickup_requested_at ? new Date(row.pickup_requested_at).toLocaleString() : "ASAP"}/>
        <IdField label="Phone" value={row.customer_phone}/>
        <IdField label="Email" value={row.customer_email}/>
        <div className="col-span-2">
          <IdField label="Drop-off" value={row.dropoff_address}/>
        </div>
      </div>

      {/* Per-stop panels · receipts + item counts + orders */}
      <div className="mt-4 border-t border-zinc-800 pt-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
          <FileImage className="h-3 w-3"/>Stop Receipts & Manifest
        </div>
        <div className="space-y-2">
          {(row.stops || []).map((s) => (
            <StopCard key={s.index} deliveryId={row.id} stop={s}/>
          ))}
        </div>
      </div>

      {/* Itemized billing */}
      {row.quote_total != null && (
        <div className="mt-4 border-t border-zinc-800 pt-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
              <DollarSign className="h-3 w-3"/>Itemized Billing
            </div>
            <FinancialsOverrideButton row={row} onChanged={onChanged}/>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs" data-testid="doc-billing">
            <BillingRow label="Base Fee"                  value={fmtUsd(row.quote_base_fee)}/>
            <BillingRow label={`Per-stop × ${row.stop_count}`} value={fmtUsd(row.quote_per_stop_fee)}/>
            <BillingRow label={`Mileage · ${row.estimated_miles ?? 0} mi`}     value={fmtUsd(row.quote_mileage_fee)}/>
            <BillingRow label={`Time · ${row.estimated_minutes ?? 0} min`}     value={fmtUsd(row.quote_time_fee)}/>
            <BillingRow label={`Wait × ${row.stop_count}`}                     value={fmtUsd(row.quote_wait_fee)}/>
            <BillingRow label="Subtotal"                  value={fmtUsd(row.quote_subtotal ?? row.quote_fee_subtotal)} bold/>
            {row.quote_tax_amount != null && row.quote_tax_amount > 0 && (
              <BillingRow
                label={`Tax · ${row.quote_tax_rate ?? 0}%`}
                value={fmtUsd(row.quote_tax_amount)}
                testid="doc-billing-tax"
              />
            )}
            <BillingRow label={`Tip (${row.quote_tip_source || row.tip_kind})`} value={fmtUsd(row.quote_tip_amount)} accent="amber"/>
            <BillingRow label="TOTAL"                     value={fmtUsd(row.quote_total)} bold accent="emerald"/>
          </div>
          {row.admin_override && (row.admin_override.final_price != null || row.admin_override.driver_payout_amount != null) && (
            <div
              className="mt-2 p-2 rounded bg-amber-500/10 border border-amber-400/40 text-[11px] text-amber-100"
              data-testid="doc-override-banner"
            >
              <div className="flex items-center gap-1 font-bold text-amber-300 mb-1">
                <ShieldAlert className="h-3 w-3"/>ADMIN OVERRIDE APPLIED
              </div>
              {row.admin_override.final_price != null && (
                <div>
                  Final Price: <span className="font-mono font-bold text-amber-200">{fmtUsd(row.admin_override.final_price)}</span>
                  {row.quote_original_total != null && (
                    <> · was <span className="line-through text-zinc-400">{fmtUsd(row.quote_original_total)}</span></>
                  )}
                </div>
              )}
              {row.admin_override.driver_payout_amount != null && (
                <div>
                  Driver Payout Override: <span className="font-mono font-bold text-amber-200">{fmtUsd(row.admin_override.driver_payout_amount)}</span>
                </div>
              )}
              {row.admin_override.reason && (
                <div className="mt-1 text-amber-200/80">Reason: {row.admin_override.reason}</div>
              )}
              <div className="mt-0.5 text-[10px] text-amber-300/60">
                By {row.admin_override.applied_by} · {row.admin_override.applied_at?.slice(0, 19).replace("T", " ")}
              </div>
            </div>
          )}
        </div>
      )}

      {/* v2.7.63+E46 · Admin quote-insertion trigger for PENDING_QUOTE rows */}
      {row.quote_total == null && (
        <div className="mt-4 border-t border-zinc-800 pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
              <DollarSign className="h-3 w-3"/>Financials
            </div>
            <FinancialsOverrideButton row={row} onChanged={onChanged}/>
          </div>
          <div
            className="p-2 rounded bg-zinc-900 border border-dashed border-zinc-700 text-[11px] text-zinc-400"
            data-testid="doc-no-quote"
          >
            No quote yet. Insert a final price to move this delivery from PENDING_QUOTE → QUOTED.
          </div>
        </div>
      )}

      {/* v2.7.63+E46 · Internal admin-only notes thread (delivery-scoped) */}
      <AdminNotesPanel deliveryId={row.id} onChanged={onChanged}/>

      {/* v2.7.63+E46.6 · Centralized customer + driver profile notes.
          These live PERMANENTLY on the customer/driver record (not the
          delivery), so future deliveries/rides see the same context. */}
      <CustomerProfileNotesPanel deliveryId={row.id}/>
      <DriverProfileNotesPanel deliveryId={row.id} driverId={row.assigned_driver_id}/>

      {/* Payment authorization block */}
      {row.payment_id && (
        <div className="mt-4 border-t border-zinc-800 pt-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
            <CreditCard className="h-3 w-3"/>Payment Authorization
          </div>
          <div className="p-3 rounded bg-emerald-500/5 border border-emerald-400/30 text-xs" data-testid="doc-payment">
            <div className="flex items-center gap-2 text-emerald-300 font-bold mb-1">
              <CheckCircle2 className="h-4 w-4"/>{row.payment_status || "AUTHORIZED"} · {fmtUsd((row.payment_amount_cents ?? 0) / 100)}
            </div>
            <div className="text-zinc-400 space-y-0.5">
              <div>Payment ID: <span className="font-mono text-zinc-200">{row.payment_id}</span></div>
              {row.payment_receipt_url && (
                <div>
                  Receipt:{" "}
                  <a href={row.payment_receipt_url} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline inline-flex items-center gap-0.5">
                    Square hosted link<ExternalLink className="h-2.5 w-2.5"/>
                  </a>
                </div>
              )}
              <div>Authorized by: <span className="text-zinc-200">{row.payment_authorized_by}</span> · {row.payment_authorized_at?.slice(0, 19).replace("T", " ")}</div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment history (if any) */}
      {row.assigned_driver_id && (
        <div className="mt-4 border-t border-zinc-800 pt-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
            <User className="h-3 w-3"/>Assigned Driver
          </div>
          <div className="p-2 rounded bg-zinc-900 border border-zinc-800 text-xs" data-testid="doc-driver">
            <div className="font-bold text-zinc-100">{row.assigned_driver_name}</div>
            <div className="text-[10px] text-zinc-500">
              {row.assigned_driver_email} · {row.assigned_driver_phone}
            </div>
            <div className="text-[10px] text-zinc-500 mt-0.5">
              Assigned by <span className="text-zinc-300">{row.assigned_by}</span> · {row.assigned_at?.slice(0, 19).replace("T", " ")}
            </div>
          </div>
        </div>
      )}

      {/* History timeline */}
      {(row.history || []).length > 0 && (
        <div className="mt-4 border-t border-zinc-800 pt-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Timeline</div>
          <div className="space-y-1 text-[11px]" data-testid="doc-history">
            {row.history.slice().reverse().slice(0, 10).map((h, i) => (
              <div key={i} className="flex items-baseline gap-2 text-zinc-400">
                <span className="text-zinc-600 font-mono">{h.at?.slice(11, 19)}</span>
                <span className="font-bold text-zinc-300 uppercase text-[9px] tracking-wider">{h.kind}</span>
                <span className="text-zinc-500">by {h.by}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}


function StopCard({ deliveryId, stop }) {
  const [receiptOpen, setReceiptOpen] = useState(false);
  const hasReceipt = !!stop.receipt_upload_id;
  // For the auth-token'd receipt URL we hit the admin route directly.
  // adminApi handles the bearer token via axios interceptor.
  const receiptDownloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/deliveries/${deliveryId}/receipt/${stop.index}`;

  const openReceipt = async () => {
    // Fetch with bearer auth then open in a new tab via blob URL — avoids
    // triggering the browser's XHR download UX when the file is inline-able.
    try {
      const tok = localStorage.getItem("rtg_admin_token");
      const res = await fetch(receiptDownloadUrl, { headers: { Authorization: `Bearer ${tok}` }});
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Revoke after a beat to free memory but keep the tab-open working.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(`Receipt fetch failed: ${e?.message || e}`);
    }
  };

  return (
    <div className="p-3 rounded bg-zinc-900 border border-zinc-800" data-testid={`doc-stop-${stop.index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-amber-300">Stop {stop.index + 1} · {stop.business_name}</div>
          <div className="text-[11px] text-zinc-400 flex items-center gap-1 mt-0.5"><MapPin className="h-2.5 w-2.5"/>{stop.business_address}</div>
          <div className="mt-1 grid grid-cols-2 gap-2 text-[10px]">
            {stop.order_number && (
              <div className="text-zinc-500">Order # <span className="text-zinc-200 font-mono">{stop.order_number}</span></div>
            )}
            <div className="text-zinc-500">Items <span className="text-zinc-200 font-bold">{stop.item_count}</span></div>
          </div>
        </div>
        <div className="flex-shrink-0">
          {hasReceipt ? (
            <button
              type="button"
              onClick={openReceipt}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded bg-sky-500/15 border border-sky-400/40 text-sky-300 hover:bg-sky-500/25 transition"
              data-testid={`doc-stop-${stop.index}-receipt`}
              title={`${stop.receipt_filename} · ${(stop.receipt_bytes / 1024).toFixed(1)} KB`}
            >
              <FileImage className="h-3 w-3"/>Receipt
            </button>
          ) : (
            <span className="text-[10px] text-zinc-600 italic">No receipt</span>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── tiny sub-components ──
function MiniStat({ label, value, tone, testid }) {
  const t = tone === "amber" ? "text-amber-300"
    : tone === "sky" ? "text-sky-300"
    : tone === "emerald" ? "text-emerald-300"
    : tone === "red" ? "text-red-300"
    : "text-zinc-100";
  return (
    <div className="p-2 rounded bg-zinc-950 border border-zinc-800" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`meter-display font-bold text-base mt-0.5 ${t}`}>{value}</div>
    </div>
  );
}

function IdField({ label, value, mono = false }) {
  return (
    <div className="p-1.5">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-zinc-200 mt-0.5 ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function BillingRow({ label, value, bold, accent, testid }) {
  const c = accent === "emerald" ? "text-emerald-300"
    : accent === "amber" ? "text-amber-300"
    : "text-zinc-300";
  return (
    <div
      className="flex items-center justify-between py-1 border-b border-zinc-800/60"
      data-testid={testid}
    >
      <span className={`text-zinc-500 text-[11px]`}>{label}</span>
      <span className={`meter-display ${bold ? "font-bold" : ""} ${c}`}>{value ?? "—"}</span>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// v2.7.63+E46 · Pricing Transparency & Admin Overrides
// ═══════════════════════════════════════════════════════════════════════

function FinancialsOverrideButton({ row, onChanged }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="h-7 text-[10px] bg-amber-500/10 border-amber-400/40 text-amber-200 hover:bg-amber-500/25"
        data-testid={`doc-financials-edit-${row.id}`}
      >
        <Edit3 className="h-3 w-3 mr-1"/>
        {row.quote_total == null ? "Insert Quote" : "Edit Financials"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md"
          data-testid="financials-modal"
        >
          <FinancialsOverrideForm
            row={row}
            onSaved={() => { setOpen(false); onChanged && onChanged(); }}
            onCancel={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}


function FinancialsOverrideForm({ row, onSaved, onCancel }) {
  // Full-manual mode: admin enters both final_price + driver_payout_amount.
  // Proportional mode: admin enters only final_price; server scales the
  // driver payout via pricing.driver_payout_ratio at deliver-time.
  const currentTotal   = row.quote_total ?? "";
  const currentPayout  = row?.admin_override?.driver_payout_amount ?? "";
  const [mode, setMode] = useState("proportional");
  const [finalPrice, setFinalPrice] = useState(currentTotal !== "" ? String(currentTotal) : "");
  const [driverPayout, setDriverPayout] = useState(currentPayout !== "" ? String(currentPayout) : "");
  const [reason, setReason] = useState(row?.admin_override?.reason ?? "");
  const [saving, setSaving] = useState(false);

  const numFinal = parseFloat(finalPrice);
  const numPayout = parseFloat(driverPayout);
  const priceValid  = Number.isFinite(numFinal) && numFinal >= 0;
  const payoutValid = mode === "manual" ? (Number.isFinite(numPayout) && numPayout >= 0) : true;
  const reasonValid = reason.trim().length >= 3;
  const canSave = priceValid && payoutValid && reasonValid;

  const save = async () => {
    setSaving(true);
    try {
      const body = { final_price: numFinal, reason: reason.trim() };
      if (mode === "manual" && Number.isFinite(numPayout)) {
        body.driver_payout_amount = numPayout;
      }
      await adminApi.patch(`/deliveries/admin/${row.id}/financials`, body);
      toast.success("Financials updated. Deposit reconciliation will use the new numbers.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Edit3 className="h-4 w-4 text-amber-300"/>
          {row.quote_total == null ? "Insert Quote" : "Edit Financials"}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-3 mt-2 text-xs">
        <div className="p-2 rounded bg-zinc-900 border border-zinc-800 text-[11px] text-zinc-400">
          Adjusting the <span className="text-zinc-200 font-semibold">Final Price</span> mirrors into
          <span className="font-mono text-zinc-200"> quote_total</span> and feeds the End-of-Day deposit
          reconciliation. Nothing is charged from this dialog.
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1 flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-emerald-300"/>Final Price · Customer-facing
          </Label>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-400 text-xs font-bold pointer-events-none">$</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={finalPrice}
              onChange={(e) => setFinalPrice(e.target.value)}
              className="bg-zinc-900 border-zinc-700 h-10 pl-5 meter-display"
              data-testid="financials-final-price"
              placeholder="0.00"
            />
          </div>
          {row.quote_original_total != null && (
            <div className="text-[10px] text-zinc-500 mt-1">
              Original quote: {fmtUsd(row.quote_original_total)}
            </div>
          )}
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">
            Driver Payout Mode
          </Label>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setMode("proportional")}
              className={`px-2.5 py-1.5 rounded text-[10px] uppercase font-bold tracking-wider border transition ${
                mode === "proportional"
                  ? "bg-emerald-500/20 border-emerald-400 text-emerald-200"
                  : "bg-zinc-950 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              }`}
              data-testid="financials-mode-proportional"
            >
              Proportional Recalc
            </button>
            <button
              type="button"
              onClick={() => setMode("manual")}
              className={`px-2.5 py-1.5 rounded text-[10px] uppercase font-bold tracking-wider border transition ${
                mode === "manual"
                  ? "bg-amber-500/20 border-amber-400 text-amber-200"
                  : "bg-zinc-950 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              }`}
              data-testid="financials-mode-manual"
            >
              Full Manual
            </button>
          </div>
          {mode === "manual" ? (
            <div className="mt-2">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1 flex items-center gap-1">
                <User className="h-3 w-3 text-amber-300"/>Driver Payout Amount
              </Label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-400 text-xs font-bold pointer-events-none">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={driverPayout}
                  onChange={(e) => setDriverPayout(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 h-10 pl-5 meter-display"
                  data-testid="financials-driver-payout"
                  placeholder="0.00"
                />
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">
                This exact amount goes to the driver&apos;s earnings balance when the delivery closes.
              </p>
            </div>
          ) : (
            <p className="text-[10px] text-zinc-500 mt-2">
              Driver payout will be auto-calculated on delivery close using the operator&apos;s
              <span className="font-mono text-zinc-300"> driver_payout_ratio</span> against the fee subtotal
              (base + stops + miles + time + wait). Tips flow 100% to the driver.
            </p>
          )}
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1 flex items-center gap-1">
            <MessageSquare className="h-3 w-3 text-sky-300"/>Reason · required (audit trail)
          </Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Price-match Uber quote · $10 goodwill discount · Manual pickup fee adjustment"
            rows={3}
            className="bg-zinc-900 border-zinc-700 text-xs"
            data-testid="financials-reason"
          />
          {!reasonValid && reason.length > 0 && (
            <div className="text-[10px] text-red-400 mt-1">Reason must be at least 3 characters.</div>
          )}
        </div>
      </div>

      <DialogFooter className="mt-3 flex gap-2 sm:justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={saving}
          className="bg-zinc-950 border-zinc-700 text-zinc-300"
          data-testid="financials-cancel"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={!canSave || saving}
          className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold"
          data-testid="financials-save"
        >
          {saving ? "Saving…" : row.quote_total == null ? "Insert Quote" : "Save Override"}
        </Button>
      </DialogFooter>
    </>
  );
}


function AdminNotesPanel({ deliveryId, onChanged }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/deliveries/admin/${deliveryId}/notes`);
      setNotes(r.data?.notes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load notes");
    } finally { setLoading(false); }
  }, [deliveryId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const t = text.trim();
    if (!t || saving) return;
    setSaving(true);
    try {
      await adminApi.post(`/deliveries/admin/${deliveryId}/notes`, { text: t });
      setText("");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4 border-t border-zinc-800 pt-3" data-testid="admin-notes-panel">
      <div className="flex items-center gap-1 mb-2">
        <StickyNote className="h-3 w-3 text-sky-300"/>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          Internal Notes · Not visible to driver or customer
        </div>
        <div className="ml-auto text-[10px] text-zinc-600">{notes.length} note{notes.length === 1 ? "" : "s"}</div>
      </div>

      {loading ? (
        <div className="text-[11px] text-zinc-500 italic">Loading notes…</div>
      ) : notes.length === 0 ? (
        <div className="text-[11px] text-zinc-500 italic" data-testid="admin-notes-empty">
          No internal notes yet. Add one below.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="admin-notes-list">
          {notes.map((n) => (
            <div
              key={n.id}
              className={`p-2 rounded border text-[11px] ${
                n.kind === "financials_override"
                  ? "bg-amber-500/5 border-amber-400/30"
                  : "bg-zinc-900 border-zinc-800"
              }`}
              data-testid={`admin-note-${n.id}`}
            >
              <div className="text-zinc-200 whitespace-pre-wrap">{n.text}</div>
              <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-zinc-500">
                <span>{n.by}</span>
                <span className="font-mono">{n.at?.slice(0, 19).replace("T", " ")}</span>
              </div>
              {n.kind === "financials_override" && (
                <span className="mt-0.5 inline-block text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/30 uppercase tracking-widest font-bold">
                  Auto · financials
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 flex items-start gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add an internal note (drivers can't see this)…"
          rows={2}
          className="bg-zinc-900 border-zinc-700 text-xs flex-1"
          data-testid="admin-note-input"
        />
        <Button
          type="button"
          onClick={submit}
          disabled={!text.trim() || saving}
          className="bg-sky-500 hover:bg-sky-400 text-zinc-950 font-bold h-16"
          data-testid="admin-note-submit"
        >
          <Send className="h-3.5 w-3.5"/>
        </Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// v2.7.63+E46.6 · Customer + Driver profile notes
// ═══════════════════════════════════════════════════════════════════════
// These notes are ATTACHED TO THE CUSTOMER/DRIVER PROFILE — not to the
// delivery. Future deliveries and rides for the same customer/driver
// will surface these notes, making them the primary long-lived context
// store. The delivery-scoped notes (above) remain scoped to this one
// delivery and are best for one-off "customer requested contactless"
// annotations.

function CustomerProfileNotesPanel({ deliveryId }) {
  return (
    <ProfileNotesPanel
      deliveryId={deliveryId}
      kind="customer"
      title="Customer Profile Notes"
      hint="Permanent · follows the customer across ALL deliveries and rides"
      icon={<User className="h-3 w-3 text-purple-300"/>}
      accent="purple"
      endpoint="customer-notes"
      testidPrefix="customer-profile-note"
    />
  );
}


function DriverProfileNotesPanel({ deliveryId, driverId }) {
  return (
    <ProfileNotesPanel
      deliveryId={deliveryId}
      kind="driver"
      title="Driver Profile Notes"
      hint={driverId ? "Permanent · follows the driver across ALL shifts" : "Assign a driver first to see/add profile notes"}
      icon={<Truck className="h-3 w-3 text-emerald-300"/>}
      accent="emerald"
      endpoint="driver-notes"
      testidPrefix="driver-profile-note"
      disabled={!driverId}
    />
  );
}


function ProfileNotesPanel({ deliveryId, title, hint, icon, accent,
                              endpoint, testidPrefix, disabled = false }) {
  const [state, setState] = useState({ notes: [], meta: null, loading: true });
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (disabled) {
      setState({ notes: [], meta: null, loading: false });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await adminApi.get(`/deliveries/admin/${deliveryId}/${endpoint}`);
      setState({
        notes:   r.data?.notes || [],
        meta:    r.data,
        loading: false,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not load ${title.toLowerCase()}`);
      setState({ notes: [], meta: null, loading: false });
    }
  }, [deliveryId, endpoint, title, disabled]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const t = text.trim();
    if (!t || saving) return;
    setSaving(true);
    try {
      await adminApi.post(`/deliveries/admin/${deliveryId}/${endpoint}`, { text: t });
      setText("");
      await load();
      toast.success(`${title.split(" ")[0]} profile updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not add ${title.toLowerCase().split(" ")[0]} note`);
    } finally { setSaving(false); }
  };

  const accentClasses = {
    purple:  { header: "text-purple-300", pill: "bg-purple-500/10 border-purple-400/30", btn: "bg-purple-500 hover:bg-purple-400" },
    emerald: { header: "text-emerald-300", pill: "bg-emerald-500/10 border-emerald-400/30", btn: "bg-emerald-500 hover:bg-emerald-400" },
  }[accent] || { header: "text-zinc-300", pill: "bg-zinc-900 border-zinc-800", btn: "bg-zinc-500 hover:bg-zinc-400" };

  return (
    <div
      className="mt-4 border-t border-zinc-800 pt-3"
      data-testid={`${testidPrefix}s-panel`}
    >
      <div className="flex items-center gap-1 mb-1">
        {icon}
        <div className={`text-[10px] uppercase tracking-widest font-bold ${accentClasses.header}`}>
          {title}
        </div>
        <div className="ml-auto text-[10px] text-zinc-600">
          {state.notes.length} note{state.notes.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="text-[10px] text-zinc-500 mb-2">{hint}</div>

      {state.meta && (state.meta.customer_name || state.meta.driver_name) && (
        <div
          className="mb-2 text-[11px] text-zinc-400 flex items-center gap-2 flex-wrap"
          data-testid={`${testidPrefix}-header`}
        >
          <span className="text-zinc-500">Profile:</span>
          <span className="font-bold text-zinc-200">
            {state.meta.customer_name || state.meta.driver_name}
          </span>
          {state.meta.customer_phone && (
            <span className="text-zinc-500 font-mono">· {state.meta.customer_phone}</span>
          )}
        </div>
      )}

      {disabled ? (
        <div className="text-[11px] text-zinc-500 italic p-2 bg-zinc-950 border border-dashed border-zinc-700 rounded">
          {hint}
        </div>
      ) : state.loading ? (
        <div className="text-[11px] text-zinc-500 italic">Loading…</div>
      ) : state.notes.length === 0 ? (
        <div
          className="text-[11px] text-zinc-500 italic"
          data-testid={`${testidPrefix}s-empty`}
        >
          No profile notes yet.
        </div>
      ) : (
        <div
          className="space-y-1.5 max-h-56 overflow-y-auto"
          data-testid={`${testidPrefix}s-list`}
        >
          {state.notes.map((n) => (
            <div
              key={n.id}
              className={`p-2 rounded border text-[11px] ${accentClasses.pill}`}
              data-testid={`${testidPrefix}-${n.id}`}
            >
              <div className="text-zinc-200 whitespace-pre-wrap">{n.text}</div>
              <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-zinc-500">
                <span>{n.by}</span>
                <span className="font-mono">{n.at?.slice(0, 19).replace("T", " ")}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {!disabled && (
        <div className="mt-2 flex items-start gap-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Add a ${title.split(" ")[0].toLowerCase()} profile note…`}
            rows={2}
            className="bg-zinc-900 border-zinc-700 text-xs flex-1"
            data-testid={`${testidPrefix}-input`}
          />
          <Button
            type="button"
            onClick={submit}
            disabled={!text.trim() || saving}
            className={`${accentClasses.btn} text-zinc-950 font-bold h-16`}
            data-testid={`${testidPrefix}-submit`}
          >
            <Send className="h-3.5 w-3.5"/>
          </Button>
        </div>
      )}
    </div>
  );
}
